import React, { useState } from 'react';
import { HeaderPage } from '../../../components/HeaderPage/HeaderPage';
import { DASHBOARD_ROUTES } from '../../../Routes';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CompanyBranch, CompanyInfo } from '../../../data/models/company.model';
import {
  getCompanyUsers,
  ICompanyUserListRequest,
} from '../../../data/api/company-employee.api';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import {
  PurchasedEventTypes,
  selectCompany,
} from '../../../store/companySlide';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import Table from 'components/Table';
import { saveUser } from 'store/userSlide';
import { SearchTracking } from 'components/SearchTracking';
import ExportTraking from 'assets/icon-components/exportTracking';
import Button from 'components/Button';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import MultiSelectTracking from 'components/MultiSelectTracking';
import DatePickerTracking from 'components/DatePickerTracking';
import TokenService from 'data/services/token.service';
import { AxiosResponse } from 'axios';
import { buildApiPath, formatPhoneNumber } from 'utils';
import instance from 'data/services/api';
import { API_ROUTES } from 'data/ApiRoutes';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import tz from 'moment-timezone';
import CheckIcon from 'assets/icon-components/checkIcon';
import { getConversationTag } from 'data/api/user.api';
import { getAdminDashboardStatsAsync, handleTag } from 'store/intercomSlice';
import { saveNavigateLinkHeader } from 'store/navigateSlide';
import { useAppSelector } from 'store/hooks';
import Chart, { ChartItem } from '../components/Charts/Chart';
import MuiTooltip from '@mui/material/Tooltip';
import {
  getAllBranchCompaniesOverview,
  getAllEvents,
} from 'data/api/company.api';
import { handleTabs, TabEnum } from '../company-detail/CompanDetailNew';
import { useSearchParams } from 'react-router-dom';
import { PhoneStatusBadge } from 'components/PhoneStatus';
import { PHONE_STATUS_FILTER_OPTIONS } from 'utils/phonePill';

type IMiniChart = {
  title: string;
  value: number;
};

interface QueryForm {
  allBranches: string[];
  eventType?: string;
  isActive?: string[];
  isAdmin?: string[];
  tags?: any;
  startLastLoginDate?: string;
  endLastLoginDate?: string;
  phoneStatus?: string[];
}

const NO_EVENTS_FILTER_VALUE = 'none';
const ALL_EVENT_TYPES_VALUE = 'all';

const formatNumber = (num: number) => {
  const numStr = String(num);
  if (numStr.length <= 3) {
    return numStr;
  }
  const firstCommaIndex = numStr.length % 3;
  let formattedString = '';
  if (firstCommaIndex > 0) {
    formattedString += numStr.slice(0, firstCommaIndex);
    if (numStr.length > firstCommaIndex) {
      formattedString += ',';
    }
  }
  for (let i = firstCommaIndex; i < numStr.length; i += 3) {
    formattedString += numStr.slice(i, i + 3);
    if (i + 3 < numStr.length) {
      formattedString += ',';
    }
  }

  return formattedString;
};

const MiniCard = ({ title, value }: IMiniChart) => {
  const isMobile = useMediaQuery('(hover: none) and (pointer: coarse)');
  return (
    <Stack
      sx={{
        height: '150px',
        width: { xs: '248px', xl: '100%' },
        backgroundColor: '#FAF7F4',
        borderRadius: '25px',
        padding: '24px',
        boxShadow: '2px 3px 4px 0px #00000040',
      }}
    >
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 700,
          color: '#213B54',
        }}
      >
        {title}
      </Typography>

      <MuiTooltip
        enterTouchDelay={0}
        leaveDelay={isMobile ? 6000 : 0}
        placement="bottom-start"
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -14],
                },
              },
            ],
          },
        }}
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: '#000000B2',
            },
          },
        }}
        title={
          <Stack
            sx={{
              width: 'auto',
              height: 'auto',
              borderRadius: '5px',
              minWidth: 'auto',
            }}
          >
            <Stack>
              <Typography
                sx={{
                  fontWeight: 400,
                  color: '#FFFFFF',
                  fontSize: '12px',
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#FFFFFF',
                  fontSize: '14px',
                }}
              >
                {formatNumber(value)}
              </Typography>
            </Stack>
          </Stack>
        }
      >
        <Typography
          sx={{
            fontSize: '36px',
            fontWeight: 700,
            color: '#213B54',
            cursor: 'pointer',
            marginTop: '8px',
            width: 'fit-content',
          }}
        >
          {formatNumber(value)}
        </Typography>
      </MuiTooltip>
    </Stack>
  );
};

const FilterBox = ({ control, label, name, data, fullWidth }: any) => {
  return (
    <MultiSelectTracking
      name={name}
      label={label}
      control={control}
      data={data}
      size="small"
      fullWidth={fullWidth}
      variant="outlined"
      customSx={{
        form: { height: '32px !important' },
        label: {
          fontWeight: 'bold',
          fontSize: '14px',
          lineHeight: '20px',
        },
        select: {
          border: '1px solid #213B54',
          borderRadius: '10px',
          color: '#213B54',
        },
      }}
    />
  );
};

const FilterBoxBranches = ({ control, label, name, data, fullWidth }: any) => {
  return (
    <MultiSelectTracking
      name={name}
      label={label}
      control={control}
      data={data}
      size="small"
      fullWidth={fullWidth}
      variant="outlined"
      MenuProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        MenuListProps: {
          autoFocusItem: false, // Disables automatic focus
        },
      }}
      customSx={{
        form: {
          height: '32px !important',
          background: '#FAF7F4',
          width: '225px',
          borderRadius: '10px',
        },
        label: {
          fontWeight: 700,
          fontSize: '14px',
          lineHeight: '20px',
          backgroundColor: '#FAF7F4 !important',
        },
        select: {
          border: '1px solid #213B54',
          borderRadius: '10px',
          color: '#213B54',
          width: '225px',
        },
      }}
    />
  );
};

interface EventTypeOption {
  value: string;
  label: string;
  count?: number;
  dimmed?: boolean;
}

const FilterBoxEventType = ({
  control,
  label,
  name,
  options,
}: {
  control: any;
  label: string;
  name: string;
  options: EventTypeOption[];
}) => {
  const firstDimmedIndex = options.findIndex((option) => option.dimmed);
  const menuItems: JSX.Element[] = [];
  options.forEach((option, index) => {
    if (option.dimmed && index === firstDimmedIndex) {
      menuItems.push(
        <MenuItem
          key="not-yet-in-use"
          disabled
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            minHeight: 'auto',
            marginTop: '4px',
            borderTop: '1px solid #DBD8D8',
            '&.Mui-disabled': { opacity: 0.6 },
          }}
        >
          Not yet in use
        </MenuItem>,
      );
    }
    menuItems.push(
      <MenuItem
        key={option.value}
        value={option.value}
        sx={{ opacity: option.dimmed ? 0.5 : 1 }}
      >
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          width={'100%'}
          gap={2}
        >
          <span>{option.label}</span>
          {option.count !== undefined && (
            <Typography
              component="span"
              sx={{ fontSize: '13px', color: '#213B54', opacity: 0.65 }}
            >
              {formatNumber(option.count)}
            </Typography>
          )}
        </Stack>
      </MenuItem>,
    );
  });

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={ALL_EVENT_TYPES_VALUE}
      render={({ field: { ref, ...restField } }) => (
        <FormControl size="small" sx={{ width: '260px' }}>
          <InputLabel
            shrink
            sx={{
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: '20px',
              backgroundColor: '#FAF7F4 !important',
              paddingX: '4px',
            }}
          >
            {label}
          </InputLabel>
          <MuiSelect
            {...restField}
            inputRef={ref}
            value={restField.value ?? ALL_EVENT_TYPES_VALUE}
            label={label}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              MenuListProps: {
                autoFocusItem: false,
              },
            }}
            renderValue={(selected) =>
              options.find((option) => option.value === selected)?.label || ''
            }
            sx={{
              height: '32px',
              background: '#FAF7F4',
              borderRadius: '10px',
              color: '#213B54',
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #213B54',
                borderRadius: '10px',
              },
              '& .MuiSelect-icon': {
                color: '#213B54',
              },
            }}
          >
            {menuItems}
          </MuiSelect>
        </FormControl>
      )}
    />
  );
};

export const CompanyUsers = () => {
  const { company_id } = useParams();
  const companyInfo: CompanyInfo = useSelector(selectCompany);
  const theme = useTheme();
  const [companiesEmployeesRoot, setCompaniesEmployeesRoot] = useState<any>();
  const [tag, setTag] = React.useState<any[]>([]);
  const [query, setQuery] = React.useState<string>('');
  const [allBranchCompany, setAllBranchCompany] = useState<CompanyBranch[]>();
  const [allEvents, setAllEvents] = useState<PurchasedEventTypes[]>();
  const [noEventMemberCount, setNoEventMemberCount] = useState<number>();
  const [filterTable, setFilterTable] = useState<ICompanyUserListRequest>({
    sortBy: 'created_date',
    order: undefined,
    page: 0,
    pageSize: 50,
    hasBranchCompanies: false,
    query: query,
  });
  const fetchUsersRequestRef = React.useRef(0);
  const eventTypeOptionsRequestRef = React.useRef(0);
  const lastStatsRequestRef = React.useRef<string>();

  // Captured once so deep-link params survive the URL write-back effect.
  const [initialUrlParams] = useState(
    () => new URLSearchParams(window.location.search),
  );

  const [filterExport, setFilterExport] = useState<any>();
  const { handleSubmit, control, watch, setValue } = useForm<QueryForm>({
    mode: 'onChange',
    defaultValues: {
      eventType: initialUrlParams.get('eventTypeId') || ALL_EVENT_TYPES_VALUE,
    },
  });

  const dashboardStats = useAppSelector(
    (state) => state.intercom.dashboardStats,
  );

  const [uniqueMemberEngagements, setUniqueMemberEngagements] = useState<
    ChartItem[]
  >([]);
  const [messageResponseRates, setMessageResponseRates] = useState<ChartItem[]>(
    [],
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [, setSearchParams] = useSearchParams();

  const branchesHydratedRef = React.useRef(false);

  React.useEffect(() => {
    if (!allBranchCompany) return;

    const branchIds = initialUrlParams.getAll('branchId');
    const allBranchIds = allBranchCompany
      .map((item) => item.companyId)
      .filter((id): id is string => !!id);

    setValue(
      'allBranches',
      branchIds.length > 0 && branchIds.length < allBranchIds.length
        ? branchIds
        : ['', ...allBranchIds],
    );
    branchesHydratedRef.current = true;
  }, [allBranchCompany]);

  const watchedBranches = watch('allBranches');
  const watchedEventType = watch('eventType');

  React.useEffect(() => {
    const params = new URLSearchParams();

    if (watchedBranches && !watchedBranches.includes('')) {
      watchedBranches
        .filter((item) => item !== '')
        .forEach((item) => {
          params.append('branchId', item);
        });
    }

    if (watchedEventType && watchedEventType !== ALL_EVENT_TYPES_VALUE) {
      params.set('eventTypeId', watchedEventType);
    }

    setSearchParams(params, { replace: true });
  }, [watchedBranches, watchedEventType]);

  const fetchConversationTag = async () => {
    try {
      const data = await getConversationTag();
      setTag(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchAllBranchCompaniesOverview = async () => {
    try {
      const data = await getAllBranchCompaniesOverview(company_id || '');
      setAllBranchCompany(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchEventTypeOptions = async (branchIds: string[]) => {
    const requestId = eventTypeOptionsRequestRef.current + 1;
    eventTypeOptionsRequestRef.current = requestId;

    try {
      const data = await getAllEvents(company_id || '', branchIds);
      if (requestId === eventTypeOptionsRequestRef.current) {
        setAllEvents(data.events);
        setNoEventMemberCount(data.noEventMemberCount);
      }
    } catch (error) {
      if (requestId === eventTypeOptionsRequestRef.current) {
        toast.error('Unable to load event types');
        console.log(error);
      }
    }
  };

  const selectedBranchIds =
    watchedBranches && !watchedBranches.includes('')
      ? watchedBranches.filter((item) => item !== '')
      : [];
  const branchScopeKey = selectedBranchIds.join(',');

  React.useEffect(() => {
    fetchEventTypeOptions(selectedBranchIds);
  }, [branchScopeKey, company_id]);

  React.useEffect(() => {
    if (!allEvents) return;

    if (
      watchedEventType &&
      watchedEventType !== ALL_EVENT_TYPES_VALUE &&
      watchedEventType !== NO_EVENTS_FILTER_VALUE &&
      !allEvents.some((item) => item.eventId === watchedEventType)
    ) {
      setValue('eventType', ALL_EVENT_TYPES_VALUE);
    }
  }, [allEvents, watchedEventType]);

  const eventTypeOptions: EventTypeOption[] = React.useMemo(() => {
    if (!allEvents) {
      return [{ value: ALL_EVENT_TYPES_VALUE, label: 'All event types' }];
    }

    const hasCounts = allEvents.some((item) => item.memberCount !== undefined);
    const toOption = (item: PurchasedEventTypes): EventTypeOption => ({
      value: item.eventId,
      label: item.abbreviatedName || item.name,
      count: item.memberCount,
      dimmed: hasCounts && !item.memberCount,
    });
    const inUse = allEvents.filter((item) => !hasCounts || item.memberCount);
    const notInUse = hasCounts
      ? allEvents.filter((item) => !item.memberCount)
      : [];

    return [
      { value: ALL_EVENT_TYPES_VALUE, label: 'All event types' },
      ...inUse.map(toOption),
      {
        value: NO_EVENTS_FILTER_VALUE,
        label: 'Not yet enrolled',
        count: noEventMemberCount,
      },
      ...notInUse.map(toOption),
    ];
  }, [allEvents, noEventMemberCount]);

  // The engagement-window caveat only applies once a real event type is picked;
  // "All event types" and "Not yet enrolled" scope members, not a window.
  const scopedEventLabel = React.useMemo(() => {
    if (
      !watchedEventType ||
      watchedEventType === ALL_EVENT_TYPES_VALUE ||
      watchedEventType === NO_EVENTS_FILTER_VALUE
    ) {
      return undefined;
    }
    return eventTypeOptions.find((option) => option.value === watchedEventType)
      ?.label;
  }, [watchedEventType, eventTypeOptions]);

  const buildUserListFilters = (values?: QueryForm) => {
    return {
      branchCompanies: (values?.allBranches || []).filter(
        (item: string) => item !== '',
      ),
      eventType:
        values?.eventType && values.eventType !== ALL_EVENT_TYPES_VALUE
          ? values.eventType
          : undefined,
      isActive: values?.isActive,
      isAdmin: values?.isAdmin,
      tags: values?.tags,
      phoneStatus: values?.phoneStatus,
      startDate:
        values?.startLastLoginDate &&
        moment(values.startLastLoginDate).isValid()
          ? moment(values.startLastLoginDate).startOf('day').toString()
          : undefined,
      endDate:
        values?.endLastLoginDate && moment(values.endLastLoginDate).isValid()
          ? moment(values.endLastLoginDate).endOf('day').toString()
          : undefined,
    };
  };

  const fetchData = async (values?: any) => {
    const requestId = fetchUsersRequestRef.current + 1;
    fetchUsersRequestRef.current = requestId;

    try {
      if (companyInfo.companyId) {
        setFilterExport(values);

        const res: any = await getCompanyUsers(companyInfo?.companyId || '', {
          ...filterTable,
          ...buildUserListFilters(values),
          query: query,
          hasBranchCompanies: companyInfo?.isParentCompany || false,
        });
        if (requestId === fetchUsersRequestRef.current) {
          setCompaniesEmployeesRoot(res);
        }
      }
    } catch (error) {
      if (requestId === fetchUsersRequestRef.current) {
        console.log(error);
      }
    }
  };

  const handleExportCSV = (blob: any) => {
    const data = new Blob([blob], { type: 'text/csv' });
    const csvURL = window.URL.createObjectURL(data);
    const tempLink = document.createElement('a');
    tempLink.href = csvURL;
    tempLink.setAttribute('download', companyInfo?.name + '_users.csv');
    tempLink.click();
  };

  const fetch = async (companyId?: string) => {
    try {
      const accessToken = TokenService.getToken()?.access_token;
      if (!filterExport) {
        return;
      }
      const res: AxiosResponse<any> = await instance.get(
        buildApiPath({
          path: API_ROUTES.companiesUsersExportCSV(companyId || ''),
          params: {
            ...filterTable,
            ...buildUserListFilters(filterExport),
            timezone: tz.tz.guess(),
            query: query,
            hasBranchCompanies: companyInfo?.isParentCompany,
          },
        }),
        {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      handleExportCSV(res.data);
    } catch (error) {
      console.error('Error: ', error);
    }
  };

  const handleRowClick: GridEventListener<'rowClick'> = (params) => {
    dispatch(saveUser(params.row));
    window.location.href = `${DASHBOARD_ROUTES.USER_DETAIL_ID(
      params.row.userId,
    )}`;
  };

  const formatData = (data: any, index: number) => {
    const now = moment().subtract(1, 'months');
    const months = [];
    const values: any[] = [];
    const realData = data?.map((item: any) => {
      return {
        month: moment(item.createdDate)
          .utc()
          .tz('America/Chicago')
          .format('MMM'),
        percent:
          item.uniqueMemberEngagement ||
          item.messageResponseRate ||
          item.enrolledMember,
        total:
          item.countConversationsRecord ||
          item.conversationPartTypeAdminCount ||
          0,
        value:
          item.countConversationsRecordUser ||
          item.conversationPartTypeUserCount ||
          0,
      };
    });

    for (let i = 0; i < 6; i++) {
      const month = now.clone().subtract(i, 'months').format('MMM');
      months.unshift(month);
    }

    for (const month of months) {
      let item: any;
      if (realData && realData?.length > 0) {
        for (const rdata of realData) {
          if (month === rdata?.month) {
            item = {
              month: month,
              percent: rdata?.percent || '',
              total: rdata?.total || 0,
              value: rdata?.value || 0,
            };
            break;
          } else {
            item = {
              month: month,
              percent: '0',
              total: 0,
              value: 0,
            };
          }
        }
        values.push(item);
      } else {
        values.push({
          month: month,
          percent: '0',
          total: 0,
          value: 0,
        });
      }
    }

    if (index == 1) {
      setUniqueMemberEngagements(values);
    } else if (index == 2) {
      setMessageResponseRates(values);
    }
  };

  React.useEffect(() => {
    const subscription = watch(async () => await handleSubmit(fetchData)());
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch, query, companyInfo]);

  React.useEffect(() => {
    if (companyInfo.isParentCompany) {
      if (allBranchCompany) {
        handleSubmit(fetchData)();
      }
    } else {
      handleSubmit(fetchData)();
    }
  }, [companyInfo, filterTable, query]);

  React.useEffect(() => {
    if (!companyInfo.companyId) return;
    // Wait for the branch filter to hydrate so the first request isn't an
    // unscoped one that can race the branch-scoped follow-up.
    if (companyInfo.isParentCompany && !branchesHydratedRef.current) return;

    const request = {
      companyId: companyInfo.companyId,
      companies: (watchedBranches || []).filter((item) => item !== ''),
      hasBranchCompanies: companyInfo?.isParentCompany || false,
      eventType:
        watchedEventType && watchedEventType !== ALL_EVENT_TYPES_VALUE
          ? watchedEventType
          : undefined,
    };
    const requestKey = JSON.stringify(request);
    if (lastStatsRequestRef.current === requestKey) return;
    lastStatsRequestRef.current = requestKey;

    dispatch(getAdminDashboardStatsAsync(request));
  }, [watchedBranches, watchedEventType, companyInfo]);

  React.useEffect(() => {
    if (dashboardStats) {
      formatData(dashboardStats.enrollMemberData.enrollMembers, 0);
      formatData(dashboardStats.uniqueMemberData.uniqueMemberEngagements, 1);
      formatData(dashboardStats.messageResponseData.messageResponseRates, 2);
    }
  }, [dashboardStats]);

  React.useEffect(() => {
    fetchConversationTag();
    fetchAllBranchCompaniesOverview();
  }, []);

  const newColumns: GridColDef[] = [
    {
      field: 'companyName',
      headerName: 'Branch Location',
      flex: 1,
      minWidth: 200,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Typography
            sx={{
              fontSize: '12px',
            }}
          >
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Typography
            sx={{
              fontSize: '12px',
            }}
          >
            {params.row.name}
          </Typography>
        );
      },
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 200,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Stack direction="row" alignItems="center" gap={0.75}>
            <Typography
              sx={{
                fontSize: '12px',
              }}
            >
              {formatPhoneNumber(params.value)}
            </Typography>
            <PhoneStatusBadge member={params.row} />
          </Stack>
        );
      },
    },
    {
      field: 'relatedRoleName',
      headerName: 'Related To',
      flex: 1,
      minWidth: 200,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Typography
            sx={{
              fontSize: '12px',
            }}
          >
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: 'currentEvent',
      headerName: 'Current Event',
      flex: 1,
      minWidth: 240,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Typography
            sx={{
              fontSize: '12px',
            }}
          >
            {params.row?.currentEvent || ''}
          </Typography>
        );
      },
    },
    {
      field: 'conversationContactsIntercomId',
      headerName: 'Link to Intercom',
      flex: 1,
      sortable: true,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            {params.row.conversationContactsIntercomId && (
              <Link
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => event.stopPropagation()}
                to={`${process.env.REACT_APP_LINK_TO_INTERCOM_URL}${params.row?.conversationContactsIntercomId}/all-conversations`}
                style={{
                  textDecorationColor: '#213B54',
                }}
              >
                <Stack
                  direction={'row'}
                  gap={0.5}
                  sx={{
                    color: '#213B54',
                  }}
                >
                  Open
                  <OpenInNewRoundedIcon
                    sx={{
                      height: '16px',
                      width: '16px',
                    }}
                  />
                </Stack>
              </Link>
            )}
          </>
        );
      },
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1,
      minWidth: 280,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Stack width={'100%'} flexWrap={'wrap'} direction={'row'}>
            {params.row.tags &&
              params.row.tags.length > 0 &&
              params.row.tags.map((item: any) => {
                return (
                  <div
                    key={item}
                    className="!my-[4px]"
                    style={{
                      marginRight: '4px',
                    }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Typography
                      sx={{
                        color: '#356766',
                        backgroundColor: '#3567661A',
                        border: '1px solid #356766',
                        borderRadius: '20px',
                        paddingX: '8px',
                        height: '24px',
                        fontSize: '14px',
                        fontWeight: 500,
                        width: 'fit-content',
                        display: 'block',
                      }}
                      onClick={() => {
                        dispatch(saveUser(params.row));
                        dispatch(handleTag(item));
                        const fromLink =
                          DASHBOARD_ROUTES.COMPANY_ID_EMPLOYEES(company_id);
                        const toLink = DASHBOARD_ROUTES.USER_ID_CONVERSATION(
                          params.row.userId,
                        );
                        dispatch(saveNavigateLinkHeader({ fromLink, toLink }));
                        navigate(toLink);
                      }}
                      component="span"
                    >
                      {item}
                    </Typography>
                  </div>
                );
              })}
          </Stack>
        );
      },
    },
    {
      field: 'created_date',
      headerName: 'Date Added',
      flex: 1,
      minWidth: 160,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        return moment
          .utc(params.row.createdDate)
          .local()
          .format('MM/DD/YYYY, hh:mm A');
      },
    },
    {
      field: 'lastLoginDate',
      headerName: 'Last Login',
      flex: 1,
      minWidth: 160,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        return params.row.lastLoginDate
          ? moment
              .utc(params.row.lastLoginDate)
              .local()
              .format('MM/DD/YYYY, hh:mm A')
          : '';
      },
    },
    {
      field: 'is_active',
      headerName: 'Active',
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      flex: 1,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        return <>{params.row.active && <CheckIcon />}</>;
      },
    },
    {
      field: 'is_admin',
      headerName: 'Admin',
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      flex: 1,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        return <>{params.row.isAdmin && <CheckIcon />}</>;
      },
    },
  ];

  return (
    <>
      <HeaderPage
        title={{
          title: companyInfo?.name ? companyInfo?.name : '',
          hasBack: true,
        }}
        tabs={handleTabs(
          company_id || '',
          companyInfo?.isParentCompany || false,
          TabEnum.Users,
        )}
      />

      <div className="nv-container">
        <div className="section employees mb-6">
          <div
            className="tabs bg-[#FDFAF7] w-[345px]"
            style={{ marginBottom: '32px' }}
          >
            <Stack
              direction={'row'}
              justifyContent={'space-around'}
              height={'49px'}
              alignItems={'center'}
            >
              <Link
                to={DASHBOARD_ROUTES.COMPANY_ID_EMPLOYEES(company_id)}
                className="tab"
                style={{
                  color: '#213B54',
                  height: '100%',
                  alignItems: 'center',
                  borderBottom: '3px solid #213B54',
                  textAlign: 'center',
                  display: 'flex',
                  fontSize: '13px',
                  textDecoration: 'none',
                  fontWeight: 700,
                }}
              >
                Users
              </Link>
              <Link
                to={DASHBOARD_ROUTES.COMPANY_EMPLOYEES_ADD(company_id)}
                className="tab"
                style={{
                  color: '#213B54',
                  fontSize: '13px',
                  fontWeight: 700,
                  textDecoration: 'none',
                }}
              >
                Add a User
              </Link>
              <Link
                to={DASHBOARD_ROUTES.COMPANY_EMPLOYEES_IMPORT(company_id)}
                className="tab "
                style={{
                  color: '#213B54',
                  fontSize: '13px',
                  fontWeight: 700,
                  textDecoration: 'none',
                }}
              >
                Import Users
              </Link>
            </Stack>
          </div>
          <hr />
          <Stack width={'100%'} direction={'column'}>
            <Stack
              direction={'row'}
              gap={2}
              sx={{ flexWrap: 'wrap', marginBottom: '12px' }}
            >
              {companyInfo && companyInfo.isParentCompany && (
                <FilterBoxBranches
                  name="allBranches"
                  label="Branches"
                  control={control}
                  data={
                    allBranchCompany && allBranchCompany.length > 0
                      ? [
                          {
                            value: '',
                            label: 'All',
                          },
                          ...allBranchCompany.map((item: CompanyBranch) => {
                            return {
                              value: item.companyId,
                              label: item.name,
                            };
                          }),
                        ]
                      : []
                  }
                />
              )}
              <FilterBoxEventType
                name="eventType"
                label="Count engagement during"
                control={control}
                options={eventTypeOptions}
              />
            </Stack>
            {scopedEventLabel && (
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: '#213B54',
                  background: '#FAF7F4',
                  border: '1px solid #DBD8D8',
                  borderLeft: '4px solid #213B54',
                  borderRadius: '10px',
                  padding: '10px 14px',
                  marginTop: '12px',
                }}
              >
                Showing engagement from{' '}
                <strong>
                  messages sent while members were in {scopedEventLabel}
                </strong>
                . Members appear here if they had been enrolled — even if
                they&rsquo;ve since moved to a different event.
              </Typography>
            )}
            {company_id && (
              <Stack
                direction={'row'}
                width={'100%'}
                sx={{
                  flexWrap: 'wrap',
                  justifyContent: 'start',
                  marginTop:
                    companyInfo && companyInfo.isParentCompany ? '24px' : '0px',
                }}
                gap={3}
              >
                <Stack
                  direction={'row'}
                  gap={3}
                  width={{ xs: '100%', md: '50%', xl: '40%', xxl: '30%' }}
                >
                  <Stack gap={3} width={'100%'}>
                    <MiniCard
                      title={'Enrolled Members'}
                      value={
                        dashboardStats?.enrollMemberData.enrollMemberValue || 0
                      }
                    />
                    <MiniCard
                      title={'Triage Notifications'}
                      value={dashboardStats?.triages || 0}
                    />
                  </Stack>
                  <Stack gap={3} width={'100%'}>
                    <MiniCard
                      title={'Positive Feedback'}
                      value={dashboardStats?.possibleFeedback || 0}
                    />
                    <MiniCard
                      title={'Opt-Outs'}
                      value={dashboardStats?.optOuts || 0}
                    />
                  </Stack>
                </Stack>
                {dashboardStats && (
                  <Stack
                    gap={3}
                    direction={'row'}
                    width={{ xs: '100%', xl: '68%' }}
                  >
                    <Chart
                      data={uniqueMemberEngagements}
                      title={'Unique Member Engagement'}
                      value={
                        dashboardStats.uniqueMemberData.uniqueMemberValue ?? 0
                      }
                      isPercent={true}
                      subTitle1="Engaged conversations"
                      subTitle2="Total conversations"
                      valueSub1={
                        dashboardStats?.uniqueMemberData
                          ?.countConversationsRecordUser
                      }
                      valueSub2={
                        dashboardStats?.uniqueMemberData
                          ?.countConversationsRecord
                      }
                    />
                    <Chart
                      data={messageResponseRates}
                      title={'Member Response Rate'}
                      value={
                        dashboardStats.messageResponseData
                          .messageResponseRateValue ?? 0
                      }
                      isPercent={true}
                      subTitle1="Responses"
                      subTitle2="Sent"
                      valueSub1={
                        dashboardStats?.messageResponseData
                          ?.conversationPartTypeUserCount
                      }
                      valueSub2={
                        dashboardStats?.messageResponseData
                          ?.conversationPartTypeAdminCount
                      }
                    />
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
          <Stack sx={{ marginTop: '28px' }} spacing={2} width={'100%'}>
            <Stack
              sx={{
                overflowX: 'auto',
                overflowY: 'hidden',
                [theme.breakpoints.up(1480)]: {
                  flexDirection: 'row',
                  alignItems: 'center',
                },
                [theme.breakpoints.down(1480)]: {
                  flexDirection: 'column',
                  alignItems: 'start',
                },
              }}
              paddingBottom={'8px'}
              width={'100%'}
            >
              <Stack
                direction={'row'}
                spacing={2}
                alignItems={'center'}
                width={'100%'}
              >
                <SearchTracking
                  label="Search for a user"
                  onChange={(title: string) => {
                    setQuery(title);
                    setFilterTable((prev) =>
                      prev.page === 0 ? prev : { ...prev, page: 0 },
                    );
                  }}
                  debounce={800}
                />
                <Stack
                  paddingY={'8px'}
                  spacing={2}
                  direction={{ xs: 'column', xxl: 'row' }}
                >
                  <Stack direction={'row'} spacing={2}>
                    <FilterBox
                      name="isActive"
                      label="Is Active"
                      control={control}
                      data={[
                        {
                          value: 'Yes',
                          label: 'Yes',
                        },
                        {
                          value: 'No',
                          label: 'No',
                        },
                      ]}
                    />
                    <FilterBox
                      name="isAdmin"
                      label="Is Admin"
                      control={control}
                      data={[
                        {
                          value: 'Yes',
                          label: 'Yes',
                        },
                        {
                          value: 'No',
                          label: 'No',
                        },
                      ]}
                    />
                    <FilterBox
                      name="tags"
                      label="Tags"
                      control={control}
                      data={tag.map((item) => {
                        return {
                          value: item.name,
                          label: item.name,
                        };
                      })}
                    />
                  </Stack>
                  <Stack direction={'row'} spacing={2}>
                    <DatePickerTracking
                      name="startLastLoginDate"
                      label="FROM Last Login Date"
                      control={control}
                      inputProps="!text-sm !h-[32px] !rounded-[10px]"
                    />
                    <DatePickerTracking
                      name="endLastLoginDate"
                      label="TO Last Login Date"
                      control={control}
                      inputProps="!text-sm !h-[32px] !rounded-[10px]"
                    />
                    <FilterBox
                      name="phoneStatus"
                      label="Phone Status"
                      control={control}
                      data={PHONE_STATUS_FILTER_OPTIONS}
                    />
                  </Stack>
                </Stack>
              </Stack>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#213B54',
                  color: '#ffffff',
                  fontWeight: 700,
                  height: '40px',
                  width: '145px',
                  [theme.breakpoints.down(1480)]: {
                    marginTop: '12px',
                  },
                  [theme.breakpoints.up(1480)]: {
                    marginLeft: '16px',
                  },
                }}
                startIcon={
                  <ExportTraking
                    style={{
                      fontWeight: 700,
                    }}
                  />
                }
                onClick={() => {
                  if (company_id) {
                    fetch(company_id);
                  }
                }}
              >
                Export.csv
              </Button>
            </Stack>
            <Table
              getRowId={(r) => r.userId}
              loading={status === 'loading'}
              rows={
                companiesEmployeesRoot && companiesEmployeesRoot.users
                  ? companiesEmployeesRoot.users
                  : []
              }
              getRowHeight={() => 'auto'}
              columns={newColumns}
              paginationMode="server"
              sortingMode="server"
              rowCount={parseInt(
                `${companiesEmployeesRoot?.pager?.total || '0'}`,
              )}
              disableColumnFilter={true}
              disableColumnMenu={true}
              filter={filterTable}
              onFilterChange={setFilterTable}
              pageSizeOptions={[10, 25, 50, 100]}
              onRowClick={handleRowClick}
              columnVisibilityModel={{
                companyName: companyInfo.isParentCompany ?? false,
              }}
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  outline: 'none !important',
                  backgroundColor: '#F8F9FB',
                  maxHeight: '47px !important',
                  minHeight: '47px !important',
                  borderWidth: '1px 0px 1px 0px',
                  borderStyle: 'solid',
                  borderColor: '#DBD8D8',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: '#213B54',
                  fontWeight: 700,
                  fontSize: '13px',
                  lineHeight: '20px',
                  letterSpacing: '0.1px',
                },
                '& .MuiDataGrid-cell': {
                  color: '#000000',
                  fontWeight: 500,
                  fontSize: '12px !important',
                  cursor: 'pointer',
                  padding: '8px !important',
                },
                '& .MuiTablePagination-select': {
                  fontWeight: 500,
                },
                '& .MuiTablePagination-selectLabel': {
                  fontWeight: 500,
                },
                '& .MuiTablePagination-displayedRows': {
                  fontWeight: 500,
                },
                '& .MuiDataGrid-virtualScroller': {
                  overflowY: 'hidden',
                },
              }}
            />
          </Stack>
        </div>
      </div>
    </>
  );
};
